import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { FormikProps } from 'formik';

import {
  AdminLegendVariant,
  getLongTermContractAttributes,
  IAllLongTermShifts,
  ILongContractAttributes,
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  WorkshiftStatus,
} from '@pharmaplan/common';
import { nameTypes } from '../../../SelfService/Workshifts/WorkshiftForm/ViewEditContract/ViewEditContractTime/ViewEditContractTime';
import DateBox from '../../../SelfService/Workshifts/WorkshiftForm/ViewEditContract/ViewEditContractTime/DateBox';
import useViewEditContract from '../../../../hooks/workshift/useViewEditContract';
import { ChipType, TextFieldVariants } from '../../../../helpers/Constants';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { getDayMonthDateYear } from '../../../Reports/helpers';
import { serverToday } from '../../../../helpers/Functions';
import DateTimeRange from '../../../common/DateTimeRange';
import CustomChip from '../../../common/CustomChip';
import strings from '../../../../localization';
import styles from '../styles';

interface IExpressBookingLongTermTimes {
  data?: ILongContractAttributes[] | IAllLongTermShifts[];
  selectedIds: Array<string>;
  handleCheckboxClick: (shiftId: string) => () => void;
  formik: FormikProps<any>;
  isUpdate: boolean;
  type: PharmacistLegendVariant | AdminLegendVariant;
}

const ExpressBookingLongTermTimes: FC<IExpressBookingLongTermTimes> = ({
  type,
  data,
  formik,
  isUpdate,
  selectedIds,
  handleCheckboxClick,
}) => {
  const { generateTimeIntervals } = useViewEditContract();
  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const { shiftContractAttributes } = longTermAttributes ?? {};

  const statusConfig = {
    [WorkshiftStatus.Booked]: {
      type: PharmacyLegendVariant.booked,
      label: strings.booked,
    },
    [WorkshiftStatus.NotAvailable]: {
      type: PharmacyLegendVariant.booked,
      label: strings.booked,
    },
    [WorkshiftStatus.Requested]: {
      type: PharmacistLegendVariant.requested,
      label: strings.requested,
    },
    [WorkshiftStatus.Posted]: {
      type: PharmacistLegendVariant.workshift,
      label: strings.posted,
    },
  };

  const globalTimeHandler = (isStart?: boolean) =>
    (value?: string | Date | null) => {
      if (isStart) {
        formik.setValues({
          ...formik.values,
          ...generateTimeIntervals(shiftContractAttributes)(
            value as Date,
            formik.values.defaultEndTime,
          ),
        });
      } else {
        formik.setValues({
          ...formik.values,
          ...generateTimeIntervals(shiftContractAttributes)(
            formik.values.defaultStartTime,
            value as Date,
          ),
        });
      }
    };

  return (
    <Box sx={styles.timeDurationsContainer}>
      {!isUpdate && (
      <Box sx={styles.globalTimeContainer}>
        <Box sx={styles.noteContainer}>
          <Typography sx={styles.title}>{strings.setDefaultTime}</Typography>
          <Typography sx={styles.note}>{strings.defaultTimeNote}</Typography>
        </Box>

        <Box sx={styles.timeContainer}>
          <DateTimeRange
            startName="defaultStartTime"
            endName="defaultEndTime"
            formik={formik}
            startPlaceholder="00:00"
            endPlaceholder="00:00"
            customEndHandler={globalTimeHandler()}
            customStartHandler={globalTimeHandler(true)}
            variant={TextFieldVariants.filled}
            horizontal={false}
            allowValidOnly={false}
            removeMargin
          />
        </Box>
      </Box>
      )}
      <Box sx={styles.dateBoxGrid}>
        {data?.map?.((interval, index) => {
          let Chip;
          let disableDateTime = false;
          const {
            startDate,
            status: shiftStatus,
            workShiftId,
          } = (interval as any) ?? {};
          const date = startDate?.split?.('T')?.[0];

          if (shiftStatus && workShiftId) {
            const { label, type: chipType } = statusConfig[shiftStatus as keyof typeof statusConfig];
            const showUnfulfilled = startDate
              < (serverToday()?.toISO({ includeOffset: false }) ?? '')
            && type === PharmacistLegendVariant.workshift
            && shiftStatus !== WorkshiftStatus.Booked;

            if (showUnfulfilled) {
              Chip = (
                <CustomChip
                  type={ChipType.notAvailable}
                  label={strings.unfulfilled}
                />
              );
            } else {
              Chip = <CustomChip type={chipType} label={label} />;
            }

            disableDateTime = showUnfulfilled
            || shiftStatus === WorkshiftStatus.Booked;
          }

          const hideDayOff = index === 0
            || index === shiftContractAttributes.length - 1
            || isUpdate;

          return (
            <DateBox
              Chip={Chip}
              key={date}
              checkboxValue={selectedIds?.includes(workShiftId ?? '')}
              handleCheckboxClick={handleCheckboxClick(workShiftId ?? '')}
              showCheckbox={isUpdate && !disableDateTime}
              changedName={`${date}|${nameTypes.changed}`}
              title={`${getDayMonthDateYear(date)}`}
              startName={`${date}|${nameTypes.start}`}
              endName={`${date}|${nameTypes.end}`}
              formik={formik}
              hideDayOff={hideDayOff}
              disabled={disableDateTime}
              dayOffName={`${date}|${nameTypes.dayOff}`}
            />
          );
        })}
      </Box>
    </Box>
  );
};
export default ExpressBookingLongTermTimes;
